import {
    PathwayTemplate as PathwayTemplateDto,
    ScheduledMessageTemplate,
} from "@accurx/api/patient-messaging";
import {
    Log,
    getApiEndpoint,
    httpClient,
    returnDataOrThrow,
} from "@accurx/shared";
import { QueryKey, UseQueryOptions, useQuery } from "@tanstack/react-query";
import type {
    PathwayTemplate,
    ScheduledTemplate,
    TimeOffset,
} from "domains/message-templates/types/PathwayTemplate";
import { mapSnomedCodes } from "domains/message-templates/utils/mapSnomedCodes";

import { mapTemplatePathToHeading } from "../utils/mapTemplatePathToHeading";

type PathwayTemplateResponse = PathwayTemplateDto[];

/**
 * Calculates the number of days and time of that day that the message should be scheduled from now
 * @example 288000000000 is 8 hours so it should be sent today at 8am
 * @example 2844000000000 is 3 days and 7 hours so it should be sent in 3 days at 7am
 */
const convertDelayToTimeOffset = (delayInNs: number): TimeOffset => {
    const delayInMs = delayInNs / 10_000; // Convert to milliseconds
    const delayInHours = Math.floor(delayInMs / 3600000);
    const delayInDays = Math.floor(delayInHours / 24);
    const remainingHours = delayInHours % 24;

    return {
        days: delayInDays,
        timeOfDay: remainingHours,
    };
};

const mapScheduledMessageTemplate = (
    message: ScheduledMessageTemplate,
): ScheduledTemplate => {
    if (message.order === null || message.order === undefined) {
        Log.warn(
            "Order of the pathway message did not come through correctly.",
            {
                tags: {
                    product: "Compose",
                    pathwayMessageTitle: message.title,
                },
            },
        );
    }

    return {
        title: message.title ?? "",
        body: message.body ?? "",
        timeOffset: convertDelayToTimeOffset(message.delaySpan?.ticks ?? 0),
        order: message.order ?? null,
    };
};

const mapPathwayTemplateResponse = (
    response: PathwayTemplateResponse,
): PathwayTemplate[] => {
    return response
        .map<PathwayTemplate | null>((pathway) => {
            if (!pathway.id) {
                Log.error(
                    "Unable to map message pathway with no ID. Discarding pathway.",
                    {
                        tags: {
                            product: "Compose",
                            pathwayId: pathway.id,
                        },
                    },
                );
                return null;
            }

            if (!pathway.title) {
                Log.error(
                    "Unable to map message pathway with no title. Discarding pathway.",
                    {
                        tags: {
                            product: "Compose",
                            pathwayId: pathway.id,
                        },
                    },
                );
                return null;
            }

            if (
                !pathway.scheduledMessageTemplates ||
                pathway.scheduledMessageTemplates.length === 0
            ) {
                Log.error("Unable to map message template with no messages", {
                    tags: {
                        product: "Compose",
                        pathwayId: pathway.id,
                    },
                });
                return null;
            }

            return {
                id: `pathway-${pathway.id}`,
                heading: mapTemplatePathToHeading(pathway.path) ?? undefined,
                title: pathway.title,
                messages: pathway.scheduledMessageTemplates.map(
                    mapScheduledMessageTemplate,
                ),
                owner: "Accurx" as const,
                snomedCodes: mapSnomedCodes(pathway.snomedCodes, pathway.id),
            };
        })
        .filter((t): t is PathwayTemplate => t !== null);
};

export const usePathwayTemplatesQuery = (
    { workspaceId }: { workspaceId: number },
    options?: UseQueryOptions<
        PathwayTemplateResponse,
        Error,
        PathwayTemplate[]
    >,
) => {
    return useQuery<
        PathwayTemplateResponse,
        Error,
        PathwayTemplate[],
        QueryKey
    >(
        ["PathwayTemplates", { workspaceId }],
        async () => {
            const response = await httpClient.getReturnJsonSafeAsync(
                getApiEndpoint({
                    path: `/api/patientmessaging/template/pathway/${workspaceId}/accurx`,
                }),
            );

            return returnDataOrThrow<PathwayTemplateResponse>(response);
        },
        {
            ...options,
            select: mapPathwayTemplateResponse,
        },
    );
};
